import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui';
import { Logger } from '../../utils/logger';
import { notifyError } from '../../services/notificationService';

const log = Logger.create('PromptClipboardModal');

/**
 * PromptClipboardModal - Modal to display and copy LLM prompt
 * 
 * Shown when user clicks "Generate" with clipboard provider.
 * Displays the full prompt text with copy button.
 */
function PromptClipboardModal({ isOpen, onClose, systemPrompt, userPrompt }) {
  const [copied, setCopied] = useState(false);
  const [copiedType, setCopiedType] = useState(null);

  const handleCopy = async () => {
    const fullPrompt = `SYSTEM PROMPT:\n${systemPrompt}\n\nUSER PROMPT:\n${userPrompt}`;
    
    try {
      await navigator.clipboard.writeText(fullPrompt);
      setCopied(true);
      setCopiedType('all');
      setTimeout(() => { setCopied(false); setCopiedType(null); }, 2000);
    } catch (err) {
      log.error('Failed to copy', err);
      notifyError('Kopieren fehlgeschlagen. Bitte manuell kopieren.');
    }
  };

  const handleCopySystemOnly = async () => {
    try {
      await navigator.clipboard.writeText(systemPrompt);
      setCopied(true);
      setCopiedType('system');
      setTimeout(() => { setCopied(false); setCopiedType(null); }, 2000);
    } catch (err) {
      log.error('Failed to copy', err);
      notifyError('Kopieren fehlgeschlagen. Bitte manuell kopieren.');
    }
  };

  const handleCopyUserOnly = async () => {
    try {
      await navigator.clipboard.writeText(userPrompt);
      setCopied(true);
      setCopiedType('user');
      setTimeout(() => { setCopied(false); setCopiedType(null); }, 2000);
    } catch (err) {
      log.error('Failed to copy', err);
      notifyError('Kopieren fehlgeschlagen. Bitte manuell kopieren.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col theme-transition">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              📋 Generierter Prompt
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Kopiere diesen Prompt und füge ihn in ChatGPT, Claude oder ein anderes LLM ein
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* System Prompt */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                System Prompt
              </label>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {systemPrompt.length} Zeichen
              </span>
            </div>
            <pre className="bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-sm text-gray-900 dark:text-gray-100 overflow-x-auto whitespace-pre-wrap font-mono">
              {systemPrompt}
            </pre>
          </div>

          {/* User Prompt */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                User Prompt
              </label>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {userPrompt.length} Zeichen
              </span>
            </div>
            <pre className="bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg p-4 text-sm text-gray-900 dark:text-gray-100 overflow-x-auto whitespace-pre-wrap font-mono">
              {userPrompt}
            </pre>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>💡 Tipp:</strong> Füge zuerst den System Prompt als "Custom Instructions" 
              in deinem LLM ein (falls verfügbar), dann den User Prompt in den Chat. 
              Oder kopiere beides zusammen mit dem Button unten.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {copied && (
              <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                ✓ {copiedType === 'all' ? 'Alles kopiert!' : copiedType === 'system' ? 'System Prompt kopiert!' : 'User Prompt kopiert!'}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleCopySystemOnly}
              variant="secondary"
            >
              System Prompt kopieren
            </Button>
            <Button
              onClick={handleCopyUserOnly}
              variant="secondary"
            >
              User Prompt kopieren
            </Button>
            <Button
              onClick={handleCopy}
              variant="primary"
            >
              Alles kopieren
            </Button>
            <Button
              onClick={onClose}
              variant="secondary"
            >
              Schließen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

PromptClipboardModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  systemPrompt: PropTypes.string.isRequired,
  userPrompt: PropTypes.string.isRequired
};

export default PromptClipboardModal;
